import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import Sidebar from './components/Sidebar';
import Notification from './components/Notification';
import ErrorBoundary from './components/ErrorBoundary';
import Onboarding from './components/Onboarding';
import Auth from './features/auth/Auth';
import './App.css';
import { LanguageProvider } from './context/LanguageContext';
import { variantConfig } from './config/appVariant';

// Which feature routes ship in this build (teacher vs. general employee).
const features = variantConfig.features;
// Route for the shared document editor — neutral '/documents' in the general
// build, '/homework-generator' in the teacher build.
const DOC_PATH = variantConfig.documentPath;

// Route components are code-split with React.lazy so the initial bundle stays
// small. Each feature's heavy libraries (markdown, katex, syntax-highlighter,
// charts, pdf, etc.) now download only when that route is first visited,
// rather than all up-front on the first page load.
const Chat = lazy(() => import('./features/chat/Chat'));
const KnowledgeBases = lazy(() => import('./features/knowledge-bases/KnowledgeBases'));
const BookViewer = lazy(() => import('./features/knowledge-bases/BookViewer'));
const HomeworkGenerator = lazy(() => import('./features/homework-generator/HomeworkGenerator'));
const HomeworkCorrector = lazy(() => import('./features/homework-corrector/HomeworkCorrector'));
const ContentGenerator = lazy(() => import('./features/content-generator/Activitiespage'));
const WorksheetPage = lazy(() => import('./features/content-generator/Worksheetpage'));
const LessonPlanPage = lazy(() => import('./features/content-generator/Lessonplanpage'));
const PresentationPage = lazy(() => import('./features/content-generator/Presentationpage'));
const SuggestedVideosPage = lazy(() => import('./features/content-generator/Suggestedvideospage'));
const DeepAnalysis = lazy(() => import('./features/deep-analysis/DeepAnalysis'));
const VoiceAssistant = lazy(() => import('./features/voice-assistant/VoiceAssistant'));
const DataAnalysis = lazy(() => import('./features/data-analysis/DataAnalysis'));
const Tasks = lazy(() => import('./features/tasks/Tasks'));
const Scholarships = lazy(() => import('./features/scholarships/Scholarships'));
const Feedback = lazy(() => import('./features/feedback/Feedback'));

// Lightweight, self-contained fallback shown while a lazy route chunk loads.
// Uses an inline SVG spinner (SMIL-animated) so it needs no extra CSS.
const RouteFallback = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: '60vh'
  }}>
    <svg width="40" height="40" viewBox="0 0 50 50" role="status" aria-label="Loading">
      <circle
        cx="25" cy="25" r="20"
        fill="none" style={{ stroke: 'var(--accent-primary)' }} strokeWidth="4"
        strokeLinecap="round" strokeDasharray="90 150"
      >
        <animateTransform
          attributeName="transform" type="rotate"
          from="0 25 25" to="360 25 25"
          dur="0.9s" repeatCount="indefinite"
        />
      </circle>
    </svg>
  </div>
);


const AppContent = () => {
  const { isAuthenticated } = useApp();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [tourRun, setTourRun] = useState(0);
  const { pathname } = useLocation();
  const contentRef = useRef(null);
  const firstRoute = useRef(true);

  // A short settle-in when moving between pages. Done with the Web Animations
  // API on the container rather than a keyed remount, so pages keep their
  // state; skipped entirely under Reduce Motion.
  useEffect(() => {
    if (firstRoute.current) { firstRoute.current = false; return; }
    const el = contentRef.current;
    if (!el?.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    el.animate(
      [{ opacity: 0, transform: 'translateY(6px)' }, { opacity: 1, transform: 'none' }],
      { duration: 240, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
    );
  }, [pathname]);

  const handleMenuClick = () => {
    setIsSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
  };

  const handleOverlayClick = () => {
    setIsSidebarOpen(false);
  };

  if (!isAuthenticated) {
    return <><Auth /><Notification /></>;
  }

  return (
    <div className="app">
      <Sidebar isOpen={isSidebarOpen} onClose={handleCloseSidebar} onStartTour={() => setTourRun((n) => n + 1)} />
      
      <div className="mainLayout">
        <main className="content" ref={contentRef}>
          {/* Navigating away clears a crashed page (without remounting a healthy one) */}
          <ErrorBoundary resetKey={pathname}>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Shared by both variants */}
            <Route path="/" element={<Chat onMenuClick={handleMenuClick} />} />
            <Route path="/knowledge-bases" element={<KnowledgeBases onMenuClick={handleMenuClick} />} />
            {/* Document editor — '/documents' (general) or '/homework-generator' (teacher) */}
            <Route path={DOC_PATH} element={<HomeworkGenerator onMenuClick={handleMenuClick} />} />
            {DOC_PATH !== '/homework-generator' && (
              <Route path="/homework-generator" element={<HomeworkGenerator onMenuClick={handleMenuClick} />} />
            )}

            {/* Data Analysis (kept in both variants) */}
            {features.dataAnalysis && (
              <Route path="/data-analysis" element={<DataAnalysis onMenuClick={handleMenuClick} />} />
            )}

            {/* Scholarships / الابتعاث (both variants) */}
            {features.scholarships && (
              <Route path="/scholarships" element={<Scholarships onMenuClick={handleMenuClick} />} />
            )}

            {/* Tasks & follow-ups (both variants) */}
            {features.tasks && (
              <Route path="/tasks" element={<Tasks onMenuClick={handleMenuClick} />} />
            )}

            {/* Voice Assistant */}
            {features.voiceAssistant && (
              <Route path="/voice-assistant" element={<VoiceAssistant onMenuClick={handleMenuClick} />} />
            )}

            {/* School books reader — teacher only */}
            {features.schoolBooks && (
              <Route path="/books/view/:jobId" element={<BookViewer />} />
            )}

            {/* Homework corrector — teacher only */}
            {features.homeworkCorrector && (
              <Route path="/homework-corrector" element={<HomeworkCorrector onMenuClick={handleMenuClick} />} />
            )}

            {/* Teaching content generator tools — teacher only */}
            {features.contentGenerator && (
              <>
                <Route path="/activities" element={<ContentGenerator onMenuClick={handleMenuClick} />} />
                <Route path="/worksheet" element={<WorksheetPage onMenuClick={handleMenuClick} />} />
                <Route path="/lesson-plan" element={<LessonPlanPage onMenuClick={handleMenuClick} />} />
                <Route path="/presentation" element={<PresentationPage onMenuClick={handleMenuClick} />} />
                <Route path="/suggested-videos" element={<SuggestedVideosPage onMenuClick={handleMenuClick} />} />
                <Route path="/content-generator" element={<ContentGenerator onMenuClick={handleMenuClick} />} />
              </>
            )}

            {/* Student indicators — teacher only */}
            {features.deepAnalysis && (
              <Route path="/deep-analysis" element={<DeepAnalysis onMenuClick={handleMenuClick} />} />
            )}

            {/* Support & feedback — both variants */}
            <Route path="/feedback" element={<Feedback onMenuClick={handleMenuClick} />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
        </main>
      </div>

      <div 
        className={`overlay ${isSidebarOpen ? 'show' : ''}`}
        onClick={handleOverlayClick}
      />

      {/* Remounted by key: "Take a tour" restarts it from the first step */}
      <Onboarding key={tourRun} replay={tourRun > 0} setSidebarOpen={setIsSidebarOpen} />
      <Notification />
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <LanguageProvider>
      {/* Outer net for crashes outside the routes (login, sidebar, context) */}
      <ErrorBoundary>
      <AppProvider>
        <AppContent/>
      </AppProvider>
      </ErrorBoundary>
       </LanguageProvider>
    </Router>
  );
};

export default App;
